import { SelectField, TextAreaField } from "@/components/tool-kit/primitives";
import { t } from "@/lib/i18n/interpolate";
import {
  type DbqDocument,
  type DbqGroup,
  SOURCING_FEATURES,
  type SourcingFeature,
} from "@/lib/tool-kit/lit-essays/dbq-outline";

export interface DbqCardStrings {
  documentLabel: string;
  groupLabel: string;
  noGroup: string;
  groupOption: string;
  groupUnnamed: string;
  summaryLabel: string;
  summaryPlaceholder: string;
  sourcingLabel: string;
  sourcingNone: string;
  sourcingLabels: Readonly<Record<SourcingFeature, string>>;
  noteLabel: string;
  notePlaceholder: string;
}

/** One of the seven documents: where it goes, what it says, and how it is sourced. */
export function DbqDocumentCard({
  doc,
  groups,
  strings,
  onChange,
}: {
  doc: DbqDocument;
  groups: readonly DbqGroup[];
  strings: DbqCardStrings;
  onChange: (patch: Partial<DbqDocument>) => void;
}) {
  const groupOptions = [
    { value: "0", label: strings.noGroup },
    ...groups.map((group, index) => ({
      value: String(group.id),
      label: t(strings.groupOption, {
        n: index + 1,
        category: group.name.trim() === "" ? strings.groupUnnamed : group.name,
      }),
    })),
  ];
  const sourcingOptions: { value: SourcingFeature | ""; label: string }[] = [
    { value: "", label: strings.sourcingNone },
    ...SOURCING_FEATURES.map((value) => ({
      value,
      label: strings.sourcingLabels[value],
    })),
  ];
  const filled = doc.summary.trim() !== "";
  return (
    <details
      className="rounded-lg border border-border bg-card p-4"
      open={filled || doc.id === 1}
    >
      <summary className="cursor-pointer font-medium text-foreground text-sm">
        {t(strings.documentLabel, { n: doc.id })}
      </summary>
      <div className="mt-4 space-y-4">
        <SelectField
          id={`dbq-group-${doc.id}`}
          label={strings.groupLabel}
          onChange={(value) => onChange({ group: Number(value) })}
          options={groupOptions}
          value={String(doc.group)}
        />
        <TextAreaField
          id={`dbq-summary-${doc.id}`}
          label={strings.summaryLabel}
          onChange={(summary) => onChange({ summary })}
          placeholder={strings.summaryPlaceholder}
          prose
          rows={2}
          value={doc.summary}
        />
        <SelectField
          id={`dbq-sourcing-${doc.id}`}
          label={strings.sourcingLabel}
          onChange={(value) => onChange({ sourcing: value })}
          options={sourcingOptions}
          value={doc.sourcing}
        />
        {doc.sourcing === "" ? null : (
          <TextAreaField
            id={`dbq-note-${doc.id}`}
            label={strings.noteLabel}
            onChange={(sourcingNote) => onChange({ sourcingNote })}
            placeholder={strings.notePlaceholder}
            prose
            rows={2}
            value={doc.sourcingNote}
          />
        )}
      </div>
    </details>
  );
}
